"use client";

import { SLCardLight } from "@/components/cards/SLCardLight";
import { SLDeferredMount } from "@/components/other/SLDeferredMount";
import { SLYearOverYearBadge } from "@/components/other/SLYearOverYearBadge";
import type { Locale } from "@/lib/i18n/locales";
import { getMessages } from "@/lib/i18n/messages";
import { LocationEnum } from "@/lib/locationEnum";
import { serverTheme } from "@/lib/serverTheme";
import { fillStringTemplate } from "@/lib/utils/fillStringTemplate";
import { formatCountDe } from "@/lib/utils/formatGerman";
import { Flag } from "@mui/icons-material";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import dynamic from "next/dynamic";

const CompaniesSparkLine = dynamic(
  () => import("@/components/charts/SLSparkLine"),
  {
    ssr: false,
    loading: () => (
      <Box sx={{ minHeight: 120, bgcolor: "grey.100" }} aria-hidden />
    ),
  },
);

type TopSuburb = {
  suburbName: string;
  count: number;
  prevCount: number;
};

export function StatisticsSparklineCard({
  locale,
  city,
  location,
  effectiveYear,
  topSuburb,
  topSuburbPrev,
  priorYearLabel,
}: {
  locale: Locale;
  city: string;
  location: LocationEnum;
  effectiveYear: number;
  topSuburb: TopSuburb | null;
  topSuburbPrev: TopSuburb | null;
  priorYearLabel: string;
}) {
  const t = getMessages(locale).common.expertiseStatistics;

  return (
    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
      <SLCardLight
        title={fillStringTemplate(t.sparklineCardTitle, {
          city,
          year: effectiveYear,
        })}
        badgeLabel={t.badgeNewCompanies}
      >
        <Stack direction="column-reverse" gap={4}>
          {(topSuburb || topSuburbPrev) && (
            <Stack gap={1}>
              {topSuburb && (
                <Stack gap={2}>
                  <Typography variant="body2">
                    {fillStringTemplate(t.popularSuburbYear, {
                      year: effectiveYear,
                    })}
                  </Typography>

                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="flex-start"
                    gap={1}
                  >
                    <Flag
                      sx={{ mt: 0.1, color: serverTheme.palette.success.main }}
                    />
                    <Stack>
                      <Typography variant="body2" fontWeight={600}>
                        {topSuburb.suburbName}
                      </Typography>
                      <Stack direction="row" gap={1} alignItems="center">
                        <Typography variant="subtitle1">
                          {fillStringTemplate(t.newCompaniesCountPhrase, {
                            count: formatCountDe(topSuburb.count),
                            newCompaniesNoun: t.newCompaniesNoun,
                          })}
                        </Typography>
                        <SLYearOverYearBadge
                          current={topSuburb.count}
                          previous={topSuburb.prevCount}
                          label={priorYearLabel}
                          short
                        />
                      </Stack>
                      {topSuburbPrev && (
                        <Typography variant="subtitle1" color="text.secondary">
                          {fillStringTemplate(t.priorYearSuburb, {
                            suburbName: topSuburbPrev.suburbName,
                            count: formatCountDe(topSuburbPrev.count),
                          })}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Stack>
              )}
            </Stack>
          )}

          <Divider />

          <SLDeferredMount minHeight={120}>
            <CompaniesSparkLine
              location={location}
              year={effectiveYear}
              legalForm={undefined}
            />
          </SLDeferredMount>
        </Stack>
      </SLCardLight>
    </Grid>
  );
}
